import { ActivityLog } from "@/types/cmsTypes";
import { Card } from "@/components/ui/card";
import { CardTitle } from "@/components/ui/text";
import { formatDistanceToNow } from "date-fns";
import { CalendarDotsIcon, FileTextIcon, MapPinSimpleIcon, InfoIcon } from "@phosphor-icons/react";

interface RecentActivityProps {
  activities: ActivityLog[];
}

export function RecentActivity({ activities }: RecentActivityProps) {
  const getActivityIcon = (action: string, resource: string) => {
    if (resource === 'project') return MapPinSimpleIcon;
    return FileTextIcon;
  };

  const getActivityColor = (action: string) => {
    switch (action) {
      case 'created': return 'text-lureo-success-ink';
      case 'published': return 'text-lureo-ink';
      case 'updated': return 'text-lureo-mint-ink';
      case 'archived': return 'text-lureo-ink-subtle';
      default: return 'text-lureo-ink';
    }
  };

  const getActivityText = (activity: ActivityLog) => {
    const resourceType = activity.resource === 'project' ? 'Project' : 'Story';
    switch (activity.action) {
      case 'created': return `Created ${resourceType.toLowerCase()}`;
      case 'updated': return `Updated ${resourceType.toLowerCase()}`;
      case 'published': return `Published ${resourceType.toLowerCase()}`;
      case 'archived': return `Archived ${resourceType.toLowerCase()}`;
      default: return `Modified ${resourceType.toLowerCase()}`;
    }
  };

  return (
    <Card elevation="high" className="relative bg-lureo-neutral-subtle">
      {/* Glass Overlay */}
      <div className="absolute inset-0 bg-lureo-surface/20 rounded-lureo-radius-lg border-lureo-border-width border-lureo-highlight/30 flex items-center justify-center z-10">
        <div className="text-center p-6">
          <div className="inline-flex items-center gap-2 p-3 rounded-lureo-radius-pill bg-lureo-surface/30 backdrop-blur-lureo-blur-sm border-lureo-border-width border-lureo-highlight/40">
            <InfoIcon className="w-5 h-5 text-lureo-ink" />
            <span className="text-lureo-14 font-medium text-lureo-ink leading-5">Example Data</span>
          </div>
          <p className="text-lureo-14 text-lureo-ink-subtle max-w-xs leading-5">
            Example recent activity data.
            <br />
            The feature is currently under development.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <CalendarDotsIcon className="w-5 h-5 text-lureo-ink" />
        <CardTitle className="leading-7">Recent Activity</CardTitle>
      </div>
      
      <div className="space-y-3 md:space-y-4">
        {activities.slice(0, 10).map((activity) => {
          const Icon = getActivityIcon(activity.action, activity.resource);
          const color = getActivityColor(activity.action);
          
          return (
            <div key={activity.id} className="flex items-start gap-3 p-3 md:p-4 rounded-lureo-radius-lg border-lureo-border-width border-lureo-highlight/20 bg-lureo-surface/80 backdrop-blur-lureo-blur-sm">
              <div className={`p-2 rounded-lureo-radius-lg ${color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lureo-14 text-lureo-ink leading-5">
                  <span className="font-medium">{getActivityText(activity)}</span>
                  {' '}
                  <span className="text-lureo-ink-subtle">"{activity.resourceTitle}"</span>
                </p>
                <p className="text-lureo-12 text-lureo-ink-subtle leading-4">
                  {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}